import {
	Alert,
	AlertDescription,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Field,
	FieldDescription,
	FieldLabel,
	Input,
} from "@monbits/ui";
import { AlertCircle, ExternalLink, Send } from "lucide-react";
import * as React from "react";
import { showToast } from "src/lib/toast-helper";
import notificationQueries from "src/queries/notification-queries";
import ChatIdHelpDialog from "./chat-id-help-dialog";
import { EVENT_DEFAULTS } from "./events";
import { humanizeTelegramError } from "./telegram-error";

const StepNumber: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div
		className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground"
		aria-hidden
	>
		{children}
	</div>
);

const RequiredMark: React.FC = () => (
	<span className="text-destructive ml-0.5" aria-hidden>
		*
	</span>
);

interface Props {
	onConnected: () => void;
}

const TelegramConnectForm: React.FC<Props> = ({ onConnected }) => {
	const botTokenId = React.useId();
	const chatIdId = React.useId();

	const upsertNotification = notificationQueries.upsertNotification();

	const [botToken, setBotToken] = React.useState("");
	const [chatId, setChatId] = React.useState("");
	const [botTokenError, setBotTokenError] = React.useState("");
	const [chatIdError, setChatIdError] = React.useState("");
	const [apiError, setApiError] = React.useState<string | null>(null);
	const [helpOpen, setHelpOpen] = React.useState(false);

	const validate = (): boolean => {
		let ok = true;
		setBotTokenError("");
		setChatIdError("");
		if (botToken.trim().length === 0) {
			setBotTokenError("Bot token is required.");
			ok = false;
		}
		if (chatId.trim().length === 0) {
			setChatIdError("Chat ID is required.");
			ok = false;
		}
		return ok;
	};

	const handleConnect = async () => {
		setApiError(null);
		if (!validate()) return;
		try {
			await upsertNotification.mutateAsync({
				channel: "telegram",
				params: {
					enabled: true,
					config: {
						bot_token: botToken.trim(),
						chat_id: chatId.trim(),
					},
					events: EVENT_DEFAULTS,
				},
			});
			showToast.success({
				message: "Telegram connected",
				description:
					"Send a test message any time to verify notifications are landing.",
			});
			onConnected();
		} catch (e) {
			const raw = e instanceof Error ? e.message : String(e);
			setApiError(humanizeTelegramError(raw));
		}
	};

	return (
		<Card>
			<CardHeader>
				<div className="flex items-start gap-3">
					<Send className="h-5 w-5 mt-0.5 text-muted-foreground" aria-hidden />
					<div>
						<CardTitle>Telegram</CardTitle>
						<CardDescription>
							Send order, payment, and withdrawal updates to a Telegram chat or
							channel.
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-6">
				{apiError && (
					<Alert variant="destructive">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>{apiError}</AlertDescription>
					</Alert>
				)}

				<ol className="space-y-6">
					<li className="flex gap-4">
						<StepNumber>1</StepNumber>
						<div className="flex-1 space-y-3 pt-0.5">
							<div>
								<p className="text-sm font-medium">Create a bot</p>
								<p className="text-xs text-muted-foreground mt-1">
									Message @BotFather on Telegram and send{" "}
									<code className="text-xs">/newbot</code>. You'll get a token
									that looks like <code className="text-xs">123456:AAH…</code>.
								</p>
							</div>
							<Button asChild variant="outline" size="sm">
								<a
									href="https://t.me/BotFather"
									target="_blank"
									rel="noreferrer"
								>
									Open @BotFather
									<ExternalLink className="h-3 w-3 ml-1.5" aria-hidden />
								</a>
							</Button>
						</div>
					</li>

					<li className="flex gap-4">
						<StepNumber>2</StepNumber>
						<div className="flex-1">
							<Field>
								<FieldLabel htmlFor={botTokenId}>
									Bot token
									<RequiredMark />
								</FieldLabel>
								<Input
									id={botTokenId}
									type="password"
									value={botToken}
									onChange={(e) => {
										setBotToken(e.target.value);
										if (botTokenError) setBotTokenError("");
										if (apiError) setApiError(null);
									}}
									onBlur={() => {
										if (botToken.trim().length === 0) {
											setBotTokenError("Bot token is required.");
										}
									}}
									placeholder="123456789:AAH..."
									autoComplete="off"
									aria-required="true"
									aria-invalid={botTokenError.length > 0}
									className={botTokenError ? "border-destructive" : ""}
								/>
								{botTokenError ? (
									<FieldDescription className="text-destructive">
										{botTokenError}
									</FieldDescription>
								) : (
									<FieldDescription>
										Paste the token @BotFather sent you.
									</FieldDescription>
								)}
							</Field>
						</div>
					</li>

					<li className="flex gap-4">
						<StepNumber>3</StepNumber>
						<div className="flex-1">
							<Field>
								<FieldLabel htmlFor={chatIdId}>
									Chat ID
									<RequiredMark />
								</FieldLabel>
								<Input
									id={chatIdId}
									type="text"
									value={chatId}
									onChange={(e) => {
										setChatId(e.target.value);
										if (chatIdError) setChatIdError("");
										if (apiError) setApiError(null);
									}}
									onBlur={() => {
										if (chatId.trim().length === 0) {
											setChatIdError("Chat ID is required.");
										}
									}}
									placeholder="-1001234567890"
									autoComplete="off"
									aria-required="true"
									aria-invalid={chatIdError.length > 0}
									className={chatIdError ? "border-destructive" : ""}
								/>
								{chatIdError ? (
									<FieldDescription className="text-destructive">
										{chatIdError}
									</FieldDescription>
								) : (
									<FieldDescription>
										The chat or channel the bot should post to.{" "}
										<button
											type="button"
											onClick={() => setHelpOpen(true)}
											className="underline hover:no-underline"
										>
											How to find this
										</button>
									</FieldDescription>
								)}
							</Field>
						</div>
					</li>
				</ol>

				<div className="flex justify-end pt-2 border-t">
					<Button
						onClick={handleConnect}
						disabled={upsertNotification.isPending}
					>
						{upsertNotification.isPending ? "Connecting…" : "Connect Telegram"}
					</Button>
				</div>
			</CardContent>

			<ChatIdHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
		</Card>
	);
};

export default TelegramConnectForm;
